import { ChevronDown, Rocket } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
} from '@design-system';
import type { ReplyListEntry } from '../api/api';
import { readyToPublish, usePlaceholders, usePublishAllReady } from '../hooks/use-magic-email-library';

const replies = (count: number) => (count === 1 ? '1 reply' : `${count} replies`);

/**
 * Getting replies live in bulk: publish every reply with nothing in its way, and write the words a
 * placeholder stands for (the agency's name, a support link…) once, into every reply that has it.
 */
export function GoLivePanel({ entries }: { entries: ReplyListEntry[] }) {
  const ready = readyToPublish(entries);
  const live = entries.filter(entry => entry.status === 'live').length;
  const publishAll = usePublishAllReady();
  const { query, fill } = usePlaceholders();
  const placeholders = query.data?.placeholders ?? [];
  const needWords = new Set(placeholders.flatMap(placeholder => placeholder.entryIds)).size;

  if (ready.length === 0 && placeholders.length === 0) return null;

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[#D9D0FB] bg-[#FBFAFF] px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-base font-semibold">Get replies live</h2>
          <p className="text-[13px] text-zinc-600">
            {ready.length > 0
              ? `${replies(ready.length)} ${ready.length === 1 ? 'has' : 'have'} nothing in the way and can go live now.`
              : live > 0
                ? 'Every reply that is ready is live.'
                : 'No reply is ready to go live yet.'}
            {needWords > 0 &&
              ` ${replies(needWords)} still ${needWords === 1 ? 'has' : 'have'} words to write in.`}
          </p>
        </div>
        {ready.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                className="h-11 font-semibold"
                disabled={publishAll.isPending}
              >
                <Rocket aria-hidden /> {publishAll.isPending ? 'Publishing…' : `Publish all ready (${ready.length})`}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Publish {replies(ready.length)}?</AlertDialogTitle>
                <AlertDialogDescription>
                  From the next email, the assistant can answer advisors with these replies. You can turn any of them
                  off again on its own page.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => publishAll.mutate()}>Publish</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {placeholders.length > 0 && (
        <Collapsible className="rounded-lg border border-zinc-200 bg-white px-3">
          <CollapsibleTrigger className="group flex min-h-11 w-full items-center justify-between text-sm font-medium">
            Words to write in ({placeholders.length})
            <ChevronDown
              className="size-4 transition-transform group-data-[state=open]:rotate-180"
              aria-hidden
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="flex flex-col gap-3 pb-3">
            <p className="text-[13px] text-zinc-600">
              Write it once and it goes into every reply that needs it, as a draft of each. Each reply keeps its own
              copy, so changing one later changes only that reply.
            </p>
            {placeholders.map(placeholder => (
              <form
                key={placeholder.field}
                className="flex flex-wrap items-center gap-2"
                onSubmit={event => {
                  event.preventDefault();
                  const words = new FormData(event.currentTarget).get('words');
                  if (typeof words === 'string' && words.trim()) fill.mutate({ field: placeholder.field, words: words.trim() });
                }}
              >
                <label
                  htmlFor={`fill-${placeholder.field}`}
                  className="w-56 shrink-0 text-sm"
                >
                  {placeholder.label}
                  <span className="block text-[12px] text-zinc-500">in {replies(placeholder.entryIds.length)}</span>
                </label>
                <Input
                  id={`fill-${placeholder.field}`}
                  name="words"
                  className="h-10 min-w-0 flex-1"
                  placeholder="The words to send"
                />
                <Button
                  type="submit"
                  variant="outline"
                  size="sm"
                  disabled={fill.isPending}
                >
                  Write in
                </Button>
              </form>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  );
}
